import React, { useState } from 'react';
import { Text, Button } from '@nextui-org/react';
import { AdminSessionDetail, AdminTurn } from '../../../types/sessions';

interface Props {
    detail: AdminSessionDetail;
}

const ConversationTab: React.FC<Props> = ({ detail }) => {
    const handleExport = () => {
        // Mock export
        console.log(`Exporting transcript for session ${detail.session.id}`);
        alert("Exporting transcript...");
    };

    return (
        <div style={{ padding: '1rem' }}>
            <Button size="sm" onClick={handleExport} css={{ marginBottom: '1rem' }}>Export transcript</Button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {detail.turns.map(turn => (
                    <TurnItem key={turn.turn_id} turn={turn} />
                ))}
            </div>
        </div>
    );
};

const TurnItem = ({ turn }: { turn: AdminTurn }) => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [showRaw, setShowRaw] = useState(false);

    const isUser = turn.speaker === 'user';
    const dateStr = new Date(turn.created_at).toLocaleTimeString([], { hour12: false });

    return (
        <div style={{ paddingLeft: isUser ? '0' : '2rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', marginBottom: '0.25rem' }}>
                <Text size={12} color="#888">[{dateStr}]</Text>
                <Text b size={13} css={{ color: isUser ? '$text' : '#0070f3' }}>
                    {turn.speaker.toUpperCase()}
                </Text>
                <Text size={12} color="#888">turn {turn.turn_number}</Text>

                {!isUser && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {turn.stage && <Text size={11} color="#666">stage={turn.stage}</Text>}
                        {turn.mode && <Text size={11} color="#666">mode={turn.mode}</Text>}
                        {turn.response_time_ms && <Text size={11} color="#666">{turn.response_time_ms}ms</Text>}
                        {(turn.token_count_input !== null && turn.token_count_output !== null) && (
                            <Text size={11} color="#666">in={turn.token_count_input}/out={turn.token_count_output}</Text>
                        )}
                    </div>
                )}
            </div>

            {!isUser && turn.user_signals && (
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.25rem', fontFamily: 'monospace' }}>
                    user_signals: {Object.entries(turn.user_signals).map(([k, v]) => `${k}=${String(v)}`).join(' ')}
                </div>
            )}

            <div style={{
                background: isUser ? '#f4f4f5' : '#e6f7ff',
                padding: '0.5rem',
                borderRadius: '4px',
                marginBottom: '0.5rem'
            }}>
                <Text size={14}>"{turn.content}"</Text>
            </div>

            {/* Turn Annotations */}
            {turn.annotations && turn.annotations.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    {turn.annotations.map((ann, i) => (
                        <div key={i} title={ann.notes as string} style={{ fontSize: '10px', background: '#e0e0e0', padding: '2px 4px', borderRadius: '4px', cursor: 'help' }}>
                            {Object.entries(ann).filter(([k]) => k !== 'notes').map(([k,v]) => `${k}=${String(v)}`).join(', ')}
                        </div>
                    ))}
                </div>
            )}

            {/* Expandables */}
            {!isUser && (
                <div style={{ display: 'flex', gap: '1rem', fontSize: '12px' }}>
                    {turn.prompt_used && (
                        <div style={{ cursor: 'pointer', color: '#0070f3' }} onClick={() => setShowPrompt(!showPrompt)}>
                            {showPrompt ? 'Hide prompt_used' : 'Show prompt_used'}
                        </div>
                    )}
                    {turn.raw_llm_response && (
                        <div style={{ cursor: 'pointer', color: '#0070f3' }} onClick={() => setShowRaw(!showRaw)}>
                            {showRaw ? 'Hide raw_llm_response' : 'Show raw_llm_response'}
                        </div>
                    )}
                </div>
            )}

            {showPrompt && (
                <pre style={{ background: '#f5f5f5', padding: '0.5rem', fontSize: '11px', overflowX: 'auto', marginTop: '0.5rem' }}>
                    {turn.prompt_used}
                </pre>
            )}

            {showRaw && (
                <pre style={{ background: '#f5f5f5', padding: '0.5rem', fontSize: '11px', overflowX: 'auto', marginTop: '0.5rem' }}>
                    {JSON.stringify(turn.raw_llm_response, null, 2)}
                </pre>
            )}
        </div>
    );
};

export default ConversationTab;